import { object, string, number, mixed, array, boolean } from "yup";

import { getDataAPI } from "@utils/api";
import { Roles } from "@utils/settings";
import { ApiResponse } from "@typeDefinitions/Api";
import { APIMethods } from "@typeDefinitions/enums";

import { LangDto } from "@client/common";

export interface GetDietitianAccountResponse {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  roles: Roles[];
  phone: PhoneDto;
  language: LangDto;
  clinic: ClinicDto | null;
  billing: BillingDto | null;
  avatar: string | null;
  chat: ChatDto | null;
  permissions: PermissionsDto;
  twoFactorEnabled: boolean;
  bite: BiteDto | null;
}

interface BiteDto {
  onboardingCompleted: boolean;
}

interface PermissionsDto {
  canViewAllPatients: boolean;
}

interface ChatDto {
  description: string | null;
}

interface PhoneDto {
  prefix: string | null;
  number: string | null;
}

interface BillingDto {
  day: number;
  currentPeriod: {
    startedAt: string;
    endedAt: string;
  };
}

export enum ClinicType {
  BASIC = 1,
  SCHOOL = 2,
}
interface ClinicDto {
  id: number;
  subscription: SubscriptionDto;
  type: ClinicType;
  whitelabel: WhitelabelDto;
  appId: string | null;
  hasApiAccess: boolean;
}

interface WhitelabelDto {
  motherClinic: null | { name: string };
  partnerClinic: boolean;
}
export interface SubscriptionDto {
  finishedAt: string | null;
  type: TypeDto;
}

interface TypeDto {
  id: number;
}

export const getDietitianAccountAPI = async (): Promise<
  ApiResponse<GetDietitianAccountResponse>
> => {
  const response = await getDataAPI("/dietitian/account", APIMethods.GET);
  return getDietitianAccountSchema.validate(response);
};

const phoneSchema = object().shape({
  prefix: string().nullable().default(null),
  number: string().nullable().default(null),
});

const typeSchema = object().shape({
  id: number().required(),
});

const subscriptionSchema = object().shape({
  finishedAt: string().nullable().default(null),
  type: typeSchema.required(),
});

const billingSchema = object().shape({
  day: number().min(1).required(),
  currentPeriod: object()
    .shape({
      startedAt: string().required(),
      endedAt: string().required(),
    })
    .required(),
});

const motherClinicSchema = object({
  name: string().required(),
});

const whitelabelSchema = object().shape({
  motherClinic: motherClinicSchema.nullable().default(null),
  partnerClinic: boolean().required(),
});
const clinicSchema = object().shape({
  id: number().required(),
  subscription: subscriptionSchema.required(),
  type: mixed<ClinicType>()
    .oneOf(Object.values(ClinicType) as ClinicType[])
    .required(),
  whitelabel: whitelabelSchema.required(),
  appId: string().nullable().default(null),
  hasApiAccess: boolean().required(),
});

const permissionsSchema = object().shape({
  canViewAllPatients: boolean().required(),
});

const chatSchema = object().shape({
  description: string().nullable().default(null),
});

const biteSchema = object().shape({
  onboardingCompleted: boolean().default(false),
});

const dietitianAccountSchema = object().shape({
  id: number().required(),
  firstName: string().required(),
  lastName: string().required(),
  email: string().required(),
  avatar: string().nullable().default(null),
  roles: array()
    .of(mixed<Roles>().oneOf(Object.values(Roles)).required())
    .required(),
  phone: phoneSchema.required(),
  language: mixed<LangDto>().oneOf(Object.values(LangDto)).required(),
  clinic: clinicSchema.nullable().default(null),
  billing: billingSchema.nullable().default(null),
  chat: chatSchema.nullable().default(null),
  permissions: permissionsSchema.required(),
  twoFactorEnabled: boolean().required(),
  bite: biteSchema.nullable().default(null),
});

export const getDietitianAccountSchema = object().shape({
  data: dietitianAccountSchema.required(),
});
